import { useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { agentClient } from "../agentClient";
import { AGENT_CONTEXTS } from "../apps";
import { matchContext } from "../contexts/registry";
import { setPageContext } from "../pageState/registry";
import type { ContextRef } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";

/**
 * The context this page shows (from the route table), reported to the server as this tab's focus
 * whenever the tab gains or loses focus or the route changes. A page without one reports null, so
 * /assistant and the lists never claim focus. It is also the context a send takes page state for
 * (`pageState/registry`). Returns the page's context.
 */
export function usePageContext(): ContextRef | null {
  const { pathname } = useLocation();
  const contextRef = useMemo(() => matchContext(AGENT_CONTEXTS, pathname), [pathname]);
  useEffect(() => {
    setPageContext(contextRef);
    return () => setPageContext(null);
  }, [contextRef]);
  // Leaving the page sends a slot change still waiting out its debounce while the slots are still
  // there: layout cleanups run before the slots' own effects take them down.
  useLayoutEffect(() => () => agentClient.flushPageState(), [contextRef]);

  const open = useSelector(
    (state: { agent: AgentState }) => state.agent.connection.status === "open"
  );

  useEffect(() => {
    if (!open) return;
    const report = () =>
      agentClient.reportFocus(
        document.visibilityState === "visible" && document.hasFocus(),
        contextRef
      );
    report();
    window.addEventListener("focus", report);
    window.addEventListener("blur", report);
    document.addEventListener("visibilitychange", report);
    return () => {
      window.removeEventListener("focus", report);
      window.removeEventListener("blur", report);
      document.removeEventListener("visibilitychange", report);
    };
  }, [contextRef, open]);

  return contextRef;
}
